import useFocusNode from "@custom-hooks/useFocusNode";
import { useSignal } from "@preact/signals-react";
import { useSize } from "ahooks";
import cls from "classnames";
import { useEffect, useRef } from "react";
import { useReactFlow, useStore } from "reactflow";
import { useHomeUsePluginContainer } from "../../../../custom-hooks/HomeUsePluginRoot";
import useEnableScroll from "../../../../custom-hooks/useEnableScroll";
import useObserverNodeSize from "../../../../custom-hooks/useObserverNodeSize";
import { NO_WHEEL } from "../constants";
import { ChatContent } from "./ChatContent";
import { ChatInput } from "./ChatInput";
import styles from "./ChatInterface.module.scss";
import { ChatInterfaceBloc, ChatType } from "./ChatInterfaceBloc";
import { ChatLoading } from "./ChatLoading";
import { ChatMessageLoading } from "./ChatMessageLoading";
import { isEmpty } from "lodash";

export function ChatInterface(props: any) {
  const bloc = new ChatInterfaceBloc();
  const { id, xPos, yPos, isInCanvas } = props;
  const { setNodes } = useReactFlow();
  const contentRef = useRef<HTMLDivElement>(null);

  const { focusToNodeBottomById } = useFocusNode();

  const { isShare } = useHomeUsePluginContainer();

  const triggerNodeChanges = useStore(state => state.triggerNodeChanges);

  const { enableScroll, mouseEnterLeave } = useEnableScroll();
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef(null);
  const isInput = useSignal<boolean>(false);

  const noWheelClassName =
    enableScroll && (props.isLongPress || props.checkNodeArr?.includes(id) || isInput.value) ? NO_WHEEL : "";
  const containerClassName = cls(styles.container, noWheelClassName);

  const containerHeightRef = useRef(0);
  const containerWidthRef = useRef(0);

  const inputHeightRef = useRef(0);

  const conatinerSize = useSize(containerRef);
  const {
    size: { height },
  } = useObserverNodeSize(inputRef);

  useEffect(() => {
    const id = isInCanvas ? props.id : props.nodeId;
    setNodes((prevList: any[]) => {
      return prevList.map(x => {
        if (x.id === id) {
          return {
            ...x,
            data: {
              ...x.data,
              textAreaValue: {
                file: x.data.textAreaValue.file,
                filename: x.data.textAreaValue.filename,
                date: x.data.textAreaValue.date,
                file_uuid: x.data.textAreaValue.file_uuid,
                chatList: bloc.chatList.value,
                preQuery: bloc.prevInput.value,
                chatHistory: bloc.chatHistoryVarible.value,
                isStreamStopped: bloc.isStreamStopped.value,
              },
            },
          };
        }
        return x;
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bloc.chatList.value, bloc.isStreamStopped.value]);

  function scrollToBottom() {
    if (!contentRef.current) {
      return;
    }
    contentRef.current.scrollTop = contentRef.current.scrollHeight - contentRef.current.clientHeight;
  }

  function onStreamingContentStopped(chatList?: ChatType[]) {
    bloc.isStreamStopped.value = true;
    if (!isEmpty(chatList)) {
      bloc.chatList.value = chatList!;
    }
    scrollToBottom();
  }

  useEffect(() => {
    // sync states between canvas and preview
    if (!isShare) {
      bloc.init(props, isShare, isInput.value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.textAreaValue.chatList, props.textAreaValue.isStreamStopped]);

  useEffect(() => {
    bloc.init(props, isShare, isInput.value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.textAreaValue.date]);

  useEffect(() => {
    if (!conatinerSize) {
      return;
    }
    containerHeightRef.current = conatinerSize.height + 20;
    containerWidthRef.current = conatinerSize.width + 20;
  }, [conatinerSize]);

  useEffect(() => {
    if (!conatinerSize) {
      return;
    }

    if (inputHeightRef.current === 0) {
      inputHeightRef.current = height;
    }

    const delta = height - inputHeightRef.current; /// 和上一次 input 的差
    const newYPef = yPos - delta / 2;
    const newHRef = containerHeightRef.current + delta;

    triggerNodeChanges([
      {
        id,
        type: "position",
        position: {
          x: xPos,
          y: newYPef,
        },
      },
      {
        id,
        type: "dimensions",
        updateStyle: true,
        resizing: true,
        dimensions: {
          width: containerWidthRef.current,
          height: newHRef,
        },
      },
    ]);

    inputHeightRef.current = height;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [height]);

  useEffect(() => {
    if (props.textAreaValue.pubsub_topic) {
      bloc.listenTopic();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.textAreaValue.pubsub_topic]);

  return bloc.isLoading.value ? (
    <div className={containerClassName} ref={containerRef}>
      <ChatLoading />
      <div className={styles.chatInptContainer}>
        <ChatInput inputText={bloc.inputText} isLoading={bloc.isMessageLoading.value} isInCanvas={isInCanvas} />
      </div>
    </div>
  ) : (
    <div
      className={containerClassName}
      ref={containerRef}
      onMouseEnter={() => mouseEnterLeave(true)}
      onMouseLeave={() => mouseEnterLeave(false)}
    >
      <div className={styles.chatContent} ref={contentRef}>
        <ChatContent
          isShare={isShare}
          onLinkClicked={() => bloc.onLinkClicked()}
          chatList={bloc.chatList.value}
          isStreamStopped={bloc.isStreamStopped.value}
          textAreaValueChatList={bloc.textAreaValueChatList.value}
          AssistantDefaultList={bloc.AssistantDefaultList.value}
          onRegenerateClicked={() => bloc.onRegenerate()}
          onRefreshHeight={() => scrollToBottom()}
          onStreamingContentStopped={chatList => onStreamingContentStopped(chatList)}
        />
      </div>
      {bloc.isMessageLoading.value && (
        <div className={styles.chatMessageLoading}>
          <ChatMessageLoading />
        </div>
      )}
      <div className={styles.chatInptContainer} ref={inputRef}>
        <ChatInput
          inputText={bloc.inputText}
          isInput={isInput}
          isLoading={bloc.isMessageLoading.value}
          isInCanvas={isInCanvas}
          onSendCliked={() => bloc.onPressEnter()}
          onFocusInput={() => {
            focusToNodeBottomById(id);
          }}
        />
      </div>
    </div>
  );
}
